import React from 'react'

import { Helmet } from 'react-helmet'
import { withRouteData } from 'react-static'
import SbEditable from 'storyblok-react'
import showdown from 'showdown'
import './KianaMissionPage.css'

const markdownConverter = new showdown.Converter()

const normalizeAssetUrl = value =>
  typeof value === 'string' && value.trim().startsWith('//')
    ? `https:${value.trim()}`
    : typeof value === 'string'
      ? value.trim()
      : ''

const isNonEmptyString = value =>
  typeof value === 'string' && value.trim().length > 0

const getBlockName = block =>
  ((block && block.component) || '')
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const getAssetUrl = value => {
  if (!value) return ''

  if (typeof value === 'string') return normalizeAssetUrl(value)

  if (typeof value === 'object') {
    return normalizeAssetUrl(
      value.filename || value.url || value.cached_url || value.src || '',
    )
  }

  return ''
}

const getImageSrc = block =>
  getAssetUrl(
    block && (block.image || block.file || block.logo || block.photo || block.avatar),
  )

const getLinkFromValue = value => {
  if (!value) return ''

  if (typeof value === 'string') {
    if (/^https?:\/\//i.test(value) || value.startsWith('/')) return value
    return `/${value}`
  }

  if (typeof value === 'object') {
    return (
      value.cached_url ||
      value.url ||
      value.link ||
      value.href ||
      (value.story && value.story.url) ||
      ''
    )
  }

  return ''
}

const toEmbedUrl = source => {
  if (!isNonEmptyString(source)) return ''

  const trimmed = source.trim()

  if (trimmed.includes('youtube.com/watch?v=')) {
    const base = trimmed.replace('watch?v=', 'embed/')
    return base.split('&')[0]
  }

  return trimmed
}

const escapeHtml = value =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

const sanitizeHref = value => {
  if (!isNonEmptyString(value)) return ''

  const href = normalizeAssetUrl(value)

  if (/^javascript:/i.test(href)) return ''

  return href
}

const storyblokRichTextToHtml = value => {
  if (!value || typeof value !== 'object') return ''

  const applyMarks = (content, marks) => {
    if (!Array.isArray(marks) || !marks.length) return content

    return marks.reduce((acc, mark) => {
      if (!mark || typeof mark !== 'object') return acc

      const type = mark.type || ''
      const attrs = mark.attrs || {}

      if (type === 'link') {
        const href = sanitizeHref(attrs.href || attrs.url || attrs.cached_url || '')
        if (!href) return acc
        const external = /^https?:\/\//i.test(href)
        return `<a href="${escapeHtml(href)}"${external ? ' target="_blank" rel="noopener noreferrer"' : ''}>${acc}</a>`
      }

      if (type === 'bold') return `<strong>${acc}</strong>`
      if (type === 'italic') return `<em>${acc}</em>`
      if (type === 'underline') return `<u>${acc}</u>`
      if (type === 'strike') return `<s>${acc}</s>`
      if (type === 'code') return `<code>${acc}</code>`

      return acc
    }, content)
  }

  const renderNode = node => {
    if (!node) return ''

    if (Array.isArray(node)) return node.map(item => renderNode(item)).join('')

    if (typeof node !== 'object') return ''

    if (node.type === 'text') {
      return applyMarks(escapeHtml(node.text || ''), node.marks)
    }

    if (node.type === 'hard_break') return '<br />'

    const children = renderNode(node.content || [])

    if (node.type === 'doc') return children
    if (node.type === 'paragraph') return `<p>${children}</p>`
    if (node.type === 'bullet_list') return `<ul>${children}</ul>`
    if (node.type === 'ordered_list') return `<ol>${children}</ol>`
    if (node.type === 'list_item') return `<li>${children}</li>`
    if (node.type === 'blockquote') return `<blockquote>${children}</blockquote>`
    if (node.type === 'heading') {
      const level = Number(node.attrs && node.attrs.level)
      const safeLevel = level >= 1 && level <= 6 ? level : 3
      return `<h${safeLevel}>${children}</h${safeLevel}>`
    }

    return children
  }

  return renderNode(value).trim()
}

const extractStoryblokRichText = value => {
  if (!value || typeof value !== 'object') return ''

  const walk = node => {
    if (!node) return ''

    if (Array.isArray(node)) {
      return node.map(item => walk(item)).join('')
    }

    if (typeof node !== 'object') return ''

    if (node.type === 'text') return node.text || ''
    if (node.type === 'hard_break') return '\n'

    const children = walk(node.content || [])

    if (
      node.type === 'paragraph' ||
      node.type === 'heading' ||
      node.type === 'list_item' ||
      node.type === 'blockquote'
    ) {
      return `${children}\n\n`
    }

    return children
  }

  return walk(value).replace(/\n{3,}/g, '\n\n').trim()
}

const toParagraphs = value =>
  isNonEmptyString(value)
    ? value
        .split(/\n{2,}/)
        .map(part => part.trim())
        .filter(Boolean)
    : []

const renderRichText = block => {
  const markdown = isNonEmptyString(block.markdown) ? block.markdown : ''
  const richTextCandidates = [
    block.richtext,
    block.rich_text,
    block.richText,
    block.text,
    block.description,
  ]

  const richTextObject =
    richTextCandidates.find(
      value => value && typeof value === 'object' && (value.type === 'doc' || value.content),
    ) || null

  const richTextHtml = storyblokRichTextToHtml(richTextObject)

  const richTextPlain = richTextCandidates
    .map(value => {
      if (typeof value === 'string') return value.trim()
      return extractStoryblokRichText(value)
    })
    .find(isNonEmptyString) || ''

  const text = isNonEmptyString(block.text)
    ? block.text
    : isNonEmptyString(block.description)
      ? block.description
      : richTextPlain

  const paragraphs = toParagraphs(text)

  if (!richTextHtml && !markdown && !text) return null

  return (
    <div className='kiana-rich-text'>
      {richTextHtml ? (
        <div className='rich-text' dangerouslySetInnerHTML={{ __html: richTextHtml }} />
      ) : null}
      {!richTextHtml && paragraphs.length
        ? paragraphs.map((paragraph, index) => <p key={`p-${index}`}>{paragraph}</p>)
        : null}
      {!richTextHtml && markdown ? (
        <div
          className='rich-text'
          dangerouslySetInnerHTML={{ __html: markdownConverter.makeHtml(markdown) }}
        />
      ) : null}
    </div>
  )
}

const renderVideo = (block, key) => {
  const source = toEmbedUrl(block.source || block.url)

  if (!source) return null

  if (source.includes('<iframe')) {
    return (
      <div
        key={key}
        className='kiana-video-wrap'
        dangerouslySetInnerHTML={{ __html: source }}
      />
    )
  }

  return (
    <div key={key} className='kiana-video-wrap'>
      <iframe
        title={key}
        className='kiana-video'
        src={source}
        frameBorder='0'
        allow='autoplay; encrypted-media; picture-in-picture'
        allowFullScreen
      />
    </div>
  )
}

const renderImage = (block, key, className = 'kiana-image') => {
  const imageSrc = getImageSrc(block)

  if (!imageSrc) return null

  return (
    <div key={key} className='kiana-image-wrap'>
      <img src={imageSrc} alt={block.title || block.name || ''} className={className} />
    </div>
  )
}

const renderButtons = (block, key) => {
  const items = []

  if (Array.isArray(block.button)) items.push(...block.button)
  if (block.label || block.link || block.url) items.push(block)

  const validItems = items
    .map((item, index) => {
      const href = getLinkFromValue(item.link || item.url)

      if (!href) return null

      return {
        item,
        index,
        href,
        label: item.label || item.text || item.title || 'Learn more',
      }
    })
    .filter(Boolean)

  if (!validItems.length) return null

  return (
    <div key={key} className='kiana-button-row'>
      {validItems.map(({ item, index, href, label }) => {
        return (
          <a
            key={`${key}-${item._uid || index}`}
            href={href}
            className='button button--green'
            target={href.startsWith('http') ? '_blank' : undefined}
            rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
          >
            {label}
          </a>
        )
      })}
    </div>
  )
}

const renderSpantext = (block, key) => {
  if (!block.spantext || !isNonEmptyString(block.spantext)) return null

  const href = block.spantextlink ? getLinkFromValue(block.spantextlink) : ''

  if (href) {
    return (
      <div key={key} className='kiana-spantext'>
        <a
          href={href}
          className='kiana-spantext-button'
          target={href.startsWith('http') ? '_blank' : undefined}
          rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
        >
          {block.spantext}
        </a>
      </div>
    )
  }

  return (
    <div key={key} className='kiana-spantext'>
      <div className='kiana-spantext-text'>{block.spantext}</div>
    </div>
  )
}

const renderMember = (block, key) => (
  <SbEditable key={key} content={block}>
    <article className='kiana-member-card'>
      {renderImage(block, `${key}-image`, 'kiana-member-image')}
      <h3 className='kiana-member-name'>{block.name || block.title || ''}</h3>
      <div className='kiana-member-description'>{renderRichText(block)}</div>
      {renderButtons(block, `${key}-buttons`)}
    </article>
  </SbEditable>
)

const isMemberLikeBlock = block => {
  const name = getBlockName(block)

  return name === 'member' || name === 'staff member' || name.includes('member')
}

const looksLikeMemberCard = block => {
  if (!block || typeof block !== 'object') return false

  const hasIdentity =
    isNonEmptyString(block.name) ||
    isNonEmptyString(block.title) ||
    isNonEmptyString(block.full_name)

  const hasImage = !!getImageSrc(block)
  const hasContent =
    isNonEmptyString(block.description) ||
    isNonEmptyString(block.text) ||
    isNonEmptyString(block.markdown)

  return hasIdentity && (hasImage || hasContent)
}

const collectMemberLikeNodes = (value, rootUid, bag = []) => {
  if (!value) return bag

  if (Array.isArray(value)) {
    value.forEach(item => collectMemberLikeNodes(item, rootUid, bag))
    return bag
  }

  if (typeof value !== 'object') return bag

  const sameAsRoot = !!(rootUid && value._uid && value._uid === rootUid)

  if (!sameAsRoot && (isMemberLikeBlock(value) || looksLikeMemberCard(value))) {
    bag.push(value)
  }

  Object.keys(value).forEach(key => {
    collectMemberLikeNodes(value[key], rootUid, bag)
  })

  return bag
}

const getMemberBlocksFromHub = block => {
  if (!block || typeof block !== 'object') return []

  const directCandidates = []

  if (Array.isArray(block.members)) directCandidates.push(...block.members)
  if (Array.isArray(block.member)) directCandidates.push(...block.member)
  if (Array.isArray(block.people)) directCandidates.push(...block.people)
  if (Array.isArray(block.items)) directCandidates.push(...block.items)
  if (Array.isArray(block.staff)) directCandidates.push(...block.staff)
  if (Array.isArray(block.team)) directCandidates.push(...block.team)
  if (Array.isArray(block.filmmakers)) directCandidates.push(...block.filmmakers)

  const nestedCandidates = collectMemberLikeNodes(block, block._uid)

  return uniqueBlocks([...directCandidates, ...nestedCandidates]).filter(item =>
    isMemberLikeBlock(item) || looksLikeMemberCard(item),
  )
}

const renderMembersHub = (block, key) => {
  const members = getMemberBlocksFromHub(block)

  const sectionTitle =
    (isNonEmptyString(block.title) && block.title.trim()) ||
    (isNonEmptyString(block.subtitle) && block.subtitle.trim()) ||
    (isNonEmptyString(block.name) && block.name.trim()) ||
    'Filmmakers'

  return (
    <SbEditable key={key} content={block}>
      <section className='kiana-section-group'>
        <h2 className='kiana-section-title'>{sectionTitle}</h2>
        {members.length ? (
          <div className='kiana-members-grid'>
            {members.map((member, index) =>
              renderMember(member, `hub-member-${member._uid || index}`),
            )}
          </div>
        ) : (
          <section className='kiana-section'>
            {renderRichText(block)}
            {renderImage(block, `${key}-image`)}
            {renderButtons(block, `${key}-buttons`)}
          </section>
        )}
      </section>
    </SbEditable>
  )
}

const renderParagraphWithImageCopy = (block, key, options = {}) => {
  const { imageLeft = false, magazine = false, largeImage = false } = options
  const imageSrc = getImageSrc(block)

  if (magazine) {
    return (
      <SbEditable key={key} content={block}>
        <section className='kiana-section kiana-magazine-block'>
          {block.title ? <h3 className='kiana-fallback-title'>{block.title}</h3> : null}
          {imageSrc ? (
            <img
              src={imageSrc}
              alt={block.title || block.name || ''}
              className={`kiana-magazine-image ${imageLeft ? 'kiana-magazine-image--left' : 'kiana-magazine-image--right'}${largeImage ? ' kiana-magazine-image--large' : ''}`}
            />
          ) : null}
          {renderRichText(block)}
          {renderButtons(block, `${key}-buttons`)}
          {renderSpantext(block, `${key}-spantext`)}
        </section>
      </SbEditable>
    )
  }

  const textContent = (
    <div className='kiana-split-text'>
      {block.title ? <h3 className='kiana-fallback-title'>{block.title}</h3> : null}
      {renderRichText(block)}
      {renderButtons(block, `${key}-buttons`)}
      {renderSpantext(block, `${key}-spantext`)}
    </div>
  )

  const imageContent = imageSrc ? (
    <div className='kiana-split-image-column'>
      <div className='kiana-split-image-wrap'>
        <img
          src={imageSrc}
          alt={block.title || block.name || ''}
          className='kiana-split-image'
        />
      </div>
    </div>
  ) : null

  return (
    <SbEditable key={key} content={block}>
      <section className='kiana-section kiana-split-block'>
        {imageLeft ? imageContent : textContent}
        {imageLeft ? textContent : imageContent}
      </section>
    </SbEditable>
  )
}

const renderContentBlock = (block, key) => {
  const name = getBlockName(block)

  if (name === 'paragraph with image copy copy') {
    return renderParagraphWithImageCopy(block, key, {
      imageLeft: false,
      magazine: true,
      largeImage: true,
    })
  }

  if (name === 'paragraph with image copy' || name.includes('paragraph with image')) {
    return renderParagraphWithImageCopy(block, key, { imageLeft: false })
  }

  if (name === 'rich text' || name === 'lead paragraph') {
    return (
      <SbEditable key={key} content={block}>
        <section className='kiana-section'>{renderRichText(block)}</section>
      </SbEditable>
    )
  }

  if (name === 'video' || name === 'external video') {
    return (
      <SbEditable key={key} content={block}>
        <section className='kiana-section'>{renderVideo(block, `${key}-video`)}</section>
      </SbEditable>
    )
  }

  if (name === 'image') {
    return (
      <SbEditable key={key} content={block}>
        <section className='kiana-section'>{renderImage(block, `${key}-image`)}</section>
      </SbEditable>
    )
  }

  if (name === 'member') {
    return renderMember(block, key)
  }

  if (name === 'members hub copy' || name.includes('members hub')) {
    return renderMembersHub(block, key)
  }

  if (name === 'button') {
    return (
      <SbEditable key={key} content={block}>
        <section className='kiana-section'>{renderButtons(block, `${key}-buttons`)}</section>
      </SbEditable>
    )
  }

  return (
    <SbEditable key={key} content={block}>
      <section className='kiana-section'>
        {block.title ? <h3 className='kiana-fallback-title'>{block.title}</h3> : null}
        {renderRichText(block)}
        {renderImage(block, `${key}-image`)}
        {renderVideo(block, `${key}-video`)}
        {renderButtons(block, `${key}-buttons`)}
      </section>
    </SbEditable>
  )
}

const isRenderableBlock = value =>
  !!(
    value &&
    typeof value === 'object' &&
    (value.component || value._uid || value.text || value.markdown || value.title)
  )

const collectBlocksFromValue = (value, blocks = []) => {
  if (!value) return blocks

  if (Array.isArray(value)) {
    value.forEach(item => collectBlocksFromValue(item, blocks))
    return blocks
  }

  if (typeof value === 'object') {
    if (isRenderableBlock(value)) {
      blocks.push(value)
    }

    Object.keys(value).forEach(key => {
      collectBlocksFromValue(value[key], blocks)
    })
  }

  return blocks
}

const uniqueBlocks = blocks => {
  const seen = new Set()

  return blocks.filter(block => {
    const key = block._uid || JSON.stringify(block)

    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

const renderPageSubtitle = (block, key) => {
  const sectionTitle = block.text || block.title || block.subtitle

  if (!isNonEmptyString(sectionTitle)) return null

  return (
    <SbEditable key={key} content={block}>
      <section className='kiana-section-group'>
        <h2 className='kiana-section-title'>{sectionTitle}</h2>
      </section>
    </SbEditable>
  )
}

const KianaMissionPage = ({ story }) => {
  const content = (story && story.content) || {}
  const topBlocks = Array.isArray(content.top) ? content.top : []
  const topUidSet = new Set(topBlocks.map(block => block && block._uid).filter(Boolean))

  const explicitHeroBlock = topBlocks.find(block => {
    const name = getBlockName(block)
    return name === 'hero' || name.includes('banner')
  })

  const fallbackHeroBlock =
    !explicitHeroBlock && topBlocks.length && getImageSrc(topBlocks[0]) ? topBlocks[0] : null

  const heroBlock = explicitHeroBlock || fallbackHeroBlock || null

  const bodyBlocks = topBlocks.filter(block => block !== heroBlock)

  const title =
    (heroBlock && (heroBlock.title || heroBlock.subtitle)) ||
    story.name ||
    'Poverty Stoplight'

  const extraBlocks = Array.isArray(content.extra_blocks) ? content.extra_blocks : []

  const nestedTopBlocks = uniqueBlocks(
    topBlocks.flatMap(block => {
      if (!block || typeof block !== 'object') return []

      return Object.keys(block).flatMap(key => collectBlocksFromValue(block[key], []))
    }),
  ).filter(block => !(block && block._uid && topUidSet.has(block._uid)))

  const additionalFieldBlocks = uniqueBlocks([
    ...nestedTopBlocks,
    ...Object.keys(content)
      .filter(
        key => key !== 'top' && key !== '_uid' && key !== 'component' && key !== 'extra_blocks',
      )
      .flatMap(key => collectBlocksFromValue(content[key], [])),
  ]).filter(block => !(block && block._uid && topUidSet.has(block._uid)))

  return (
    <div className='kiana-page'>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      {heroBlock ? (
        <SbEditable content={heroBlock}>
          <section
            className='hero hero--small kiana-text-hero'
          >
            <div className='hero__overlay' />
            <div className='wrapper'>
              <h1 className='hero__title hero--small__title'>{title}</h1>
            </div>
          </section>
        </SbEditable>
      ) : null} 

      <main className='wrapper kiana-layout'>

        {bodyBlocks.map((block, index) => {
          if (getBlockName(block) === 'page subtitle') {
            return renderPageSubtitle(block, `subtitle-${block._uid || index}`)
          }

          return renderContentBlock(block, `body-${block._uid || index}`)
        })}

        {extraBlocks.length ? (
          <React.Fragment>
            {bodyBlocks.length ? (
              <hr className='kiana-divider' aria-hidden='true' />
            ) : null}
            <section className='kiana-section-group'>
              {extraBlocks.map((block, index) =>
                renderContentBlock(block, `extra-${block._uid || index}`),
              )}
            </section>
          </React.Fragment>
        ) : null}

      </main>
    </div>
  )
}

export default withRouteData(KianaMissionPage)



// WEBPACK FOOTER //
// /opt/build/repo/src/containers/KianaMissionPage.js